import axios, { AxiosError } from 'axios';
import first from 'lodash/first';
import qs from 'qs';
import { RouteConfig } from '~/routes/routeConfig';
import { isAxiosError } from '~/utils/helpers';

export enum ErrorCodes {
  NoActiveSubscription = 'no_active_subscription',
  UnauthorizedUser = 'unauthorized_user',
}

export interface ErrorResponse {
  errors?: {
    code: ErrorCodes;
    detail: string;
  }[];
}

export const backendApiGateway = axios.create({
  baseURL: import.meta.env.VITE_API_GATEWAY_BACKEND,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/vnd.api+json',
    Accept: 'application/vnd.api+json',
  },
  paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'brackets' }),
});

const unauthorisedAllowedPathname = [
  '/login',
  '/logout',
  '/signup',
  '/reset-password',
  '/share-invoice',
  '/share-provisional-balance',
  '/share-bookkeeping-ledger',
  '/change-password',
  '/accept-invite',
];

const shouldRedirectToLogin = () => {
  const { pathname } = window.location;
  const isAllowedPathname = unauthorisedAllowedPathname.find((allowedPathname) => pathname.startsWith(allowedPathname));
  if (!isAllowedPathname) return true;

  return false;
};

export const handle401Errors = (error: AxiosError<ErrorResponse>) => {
  if (shouldRedirectToLogin()) {
    console.error(`401 on ${error.config?.url ?? 'no url'}, ${window.location.pathname}, logging out`);

    const redirectUrl = new URL(RouteConfig.Logout.buildLink(), window.location.origin);
    if (window.location.pathname !== RouteConfig.AppRoot.buildLink() || window.location.search.length !== 0) {
      redirectUrl.searchParams.set('rd', `${window.location.pathname}${window.location.search}`);
    }

    window.location.href = redirectUrl.toString();
  }
};

export const handle403Errors = (errorResponse: AxiosError<ErrorResponse>) => {
  const response = errorResponse.response?.data;
  const error = first(response?.errors);
  if (!error) return;

  if (
    error.code === ErrorCodes.NoActiveSubscription &&
    !window.location.href.includes(RouteConfig.ExpiredSubscription.buildLink()) &&
    !window.location.href.includes(RouteConfig.SignUp.buildLink())
  ) {
    // eslint-disable-next-line no-console
    console.log('redirecting to', `${window.location.origin}${RouteConfig.ExpiredSubscription.buildLink()}`);
    window.location.href = `${window.location.origin}${RouteConfig.ExpiredSubscription.buildLink()}`;
  }

  if (
    error.code === ErrorCodes.UnauthorizedUser &&
    window.location.pathname !== RouteConfig.UnauthorisedCompany.buildLink()
  ) {
    window.location.href = `${window.location.origin}${RouteConfig.UnauthorisedCompany.buildLink()}`;
  }
};

backendApiGateway.interceptors.response.use(
  (successResponse) => successResponse,
  async (error) => {
    if (isAxiosError<ErrorResponse>(error)) {
      if (error.response?.status === 401) handle401Errors(error);
      else if (error.response?.status === 403) handle403Errors(error);
    }
    return Promise.reject(error);
  },
);
